<p-confirmDialog />

<div class="card">
  <div class="card-content">

    <!-- Stats: shared .stats-cards-row / .stats-card so this page reads like the others -->
    <div class="stats-cards-row">
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ totalCardCount }}</span>
          <span class="stats-card-label">Activities</span>
          <span class="stats-card-sublabel">Tracked work</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-check-square"></i></div>
      </div>

      <!-- Overdue and Unassigned are the two counts worth acting on, so they lead here
           rather than a repeat of the project list. -->
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ overdueCardCount }}</span>
          <span class="stats-card-label">Overdue</span>
          <span class="stats-card-sublabel">Past their end date</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-exclamation-triangle"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ unassignedCardCount }}</span>
          <span class="stats-card-label">Unassigned</span>
          <span class="stats-card-sublabel">No sales owner</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-user"></i></div>
      </div>
    </div>

    <!-- Filters: shared .filter-bar, shown/hidden by the main header filter toggle -->
    <div class="filter-bar filter-bar-collapsible mb-4"
         [class.filter-bar-visible]="pageHeaderService.currentFiltersVisible">
      <div class="filter-bar-filters">
        <div class="filter-field">
          <label for="pat-owner" class="filter-label">Owner / PM</label>
          <p-multiSelect id="pat-owner" [options]="ownerOptions" [(ngModel)]="selectedOwners"
                         optionLabel="label" optionValue="value" placeholder="All owners"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true" filterPlaceholder="Search..."
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="pat-status" class="filter-label">Status</label>
          <p-multiSelect id="pat-status" [options]="statusOptions" [(ngModel)]="selectedStatuses"
                         optionLabel="label" optionValue="value" placeholder="All statuses"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field pat-filter-compact">
          <label for="pat-hide-empty" class="filter-label">Hide empty</label>
          <div class="pat-check-cell">
            <input id="pat-hide-empty" type="checkbox" [(ngModel)]="hideEmptyColumns"
                   pTooltip="Hide columns with no cards" tooltipPosition="top" />
          </div>
        </div>

        <div class="filter-field pat-filter-compact" *ngIf="canManage">
          <label class="filter-label">&nbsp;</label>
          <button type="button" class="btn-add-activity" (click)="newTask()">
            <i class="pi pi-plus" aria-hidden="true"></i> Add Activity
          </button>
        </div>

        <div class="filter-field pat-filter-compact" *ngIf="hasActiveFilters">
          <label class="filter-label">&nbsp;</label>
          <button type="button" class="btn-clear-filters" (click)="clearFilters()"
                  pTooltip="Clear all filters" tooltipPosition="top">
            <i class="pi pi-filter-slash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="loading-container loading-container--centered">
      <i class="pi pi-spin pi-spinner loading-spinner-icon"></i>
      <p>Loading the tracker...</p>
    </div>

    <!-- Board. Reuses the kanban classes from _presales.scss so it reads like the deal
         board rather than inventing a second board language. -->
    <div *ngIf="!loading" class="kanban-view kanban-view--progress">
      <div class="empty-state" *ngIf="columns.length === 0">
        <div class="empty-icon"><i class="pi pi-inbox"></i></div>
        <p class="empty-message">Nothing on the board</p>
        <p class="empty-description">
          {{ hasActiveFilters
              ? 'Clear a filter, or untick "Hide empty" to see every column'
              : 'Add an activity, or create a presales project, to get started' }}
        </p>
      </div>

      <div class="kanban-scroll" *ngIf="columns.length > 0">
        <div class="kanban-grid">
          <div class="kanban-col" *ngFor="let col of columns"
               [ngClass]="col.cssClass"
               [class.is-empty]="col.cards.length === 0">
            <div class="kanban-col-head">
              <div class="kanban-col-head-top">
                <span class="kanban-col-title">
                  <span class="dot" [ngClass]="col.cssClass"></span>{{ col.label }}
                </span>
                <span class="kanban-col-count">{{ col.cards.length }}</span>
              </div>
              <!-- The deal board puts a value subtotal here; the tracker has no money, so
                   the slot carries the count worth acting on instead. Shown only when
                   there is something overdue. -->
              <span class="kanban-col-sub pat-col-sub--overdue" *ngIf="columnOverdueCount(col) > 0">
                {{ columnOverdueCount(col) }} overdue
              </span>
            </div>

            <div class="kanban-col-body">
              <!-- Same structure and classes as the deal board's card, so the two boards
                   read as one system: badge + RAG on top, name, meta line, comments, then
                   an owner/date foot. Only the content differs. -->
              <div class="kanban-card kanban-card--rich"
                   *ngFor="let card of col.cards"
                   [class.is-overdue]="overdueDays(card) !== null"
                   [class.is-clickable]="canManage"
                   (click)="onCardClick(card)">

                <!-- Row 1: the deal this belongs to + RAG dot. On a cross-project board the
                     project is what the stage badge is on the deal board — the fact you
                     need first to place the card. -->
                <div class="kanban-card-top">
                  <span class="k-stage" [pTooltip]="card.subtitle || 'No project'"
                        tooltipPosition="top">
                    {{ card.subtitle || 'Standalone' }}
                  </span>
                  <span class="k-rag" *ngIf="card.status"
                        [ngClass]="statusClass(card.status)"
                        [pTooltip]="'Health: ' + card.status" tooltipPosition="top"></span>
                </div>

                <!-- Row 2: the activity name as the headline. -->
                <h4 class="kanban-card-title" [pTooltip]="card.name" tooltipPosition="top"
                    [tooltipOptions]="{ showDelay: 600 }">{{ card.name }}</h4>

                <!-- Row 3: client, matching the deal card's client line exactly.
                     Dropped entirely for a standalone activity: it has no project to carry a
                     client, so "No client set" would restate what the STANDALONE badge above
                     already says and spend a row saying it. Kept when the activity DOES
                     belong to a project, because then a missing client is a real gap worth
                     flagging rather than a non-applicable field. -->
                <div class="kanban-card-meta" *ngIf="card.subtitle">
                  <span class="k-client" [pTooltip]="card.clientName || 'No client set'"
                        tooltipPosition="top">
                    <i class="pi pi-building"></i>{{ card.clientName || 'No client set' }}
                  </span>
                </div>

                <!-- Row 4: progress, standing where the deal card puts value and win rate. -->
                <div class="kanban-card-value">
                  <span class="k-win" [class.k-win--high]="(card.progressPercentage || 0) >= 70"
                        pTooltip="Progress" tooltipPosition="top">
                    <span class="k-win-bar"><span class="k-win-fill"
                          [style.width.%]="card.progressPercentage || 0"></span></span>
                    {{ card.progressPercentage || 0 }}%
                  </span>
                </div>

                <!-- Description and notes share the deal card's comments treatment: two-line
                     clamp, left rule, full text on hover. -->
                <div class="kanban-card-comments" *ngIf="card.description"
                     [pTooltip]="card.description" tooltipPosition="top"
                     [tooltipOptions]="{ showDelay: 500 }">
                  {{ card.description }}
                </div>
                <div class="kanban-card-comments" *ngIf="card.comments"
                     [pTooltip]="card.comments" tooltipPosition="top"
                     [tooltipOptions]="{ showDelay: 500 }">
                  <i class="pi pi-comment pat-note-icon" aria-hidden="true"></i>{{ card.comments }}
                </div>

                <!-- Foot: who owns it, and when it lands — same shape as the deal card,
                     avatar included. -->
                <div class="kanban-card-foot">
                  <span class="k-owner" [pTooltip]="'Sales Owner: ' + (card.ownerName || 'Unassigned')"
                        tooltipPosition="top">
                    <span class="k-avatar" [style.--k-hue]="avatarHue(card.ownerName)">
                      {{ initials(card.ownerName) }}
                    </span>
                    <span class="k-owner-name">{{ card.ownerName || 'Unassigned' }}</span>
                  </span>
                  <span class="k-close k-overdue" *ngIf="overdueDays(card) !== null">
                    <i class="pi pi-exclamation-circle"></i>{{ overdueDays(card) }}d over
                  </span>
                  <span class="k-close" *ngIf="overdueDays(card) === null && card.targetDate"
                        pTooltip="End date" tooltipPosition="top">
                    <i class="pi pi-calendar"></i>{{ formatDate(card.targetDate) }}
                  </span>
                </div>
              </div>

              <div class="kanban-empty" *ngIf="col.cards.length === 0">
                Nothing here
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>

<!-- Add / edit activity -->
<p-dialog
  [visible]="showTaskDialog"
  (visibleChange)="showTaskDialog = $event"
  [modal]="true"
  styleClass="dialog-lg"
  [dismissableMask]="false"
  [closable]="true">

  <ng-template pTemplate="header">
    <div class="dialog-header-with-meta">
      <span class="dialog-header-title">
        {{ editingTaskId ? 'Edit Activity' : 'Add Activity' }}
      </span>
    </div>
  </ng-template>

  <div class="pat-form">
    <div class="pat-form-row">
      <div class="pat-field pat-field--wide">
        <label for="pat-name">Activity Name <span class="pat-required">*</span></label>
        <input id="pat-name" type="text" class="pat-input" [(ngModel)]="form.name"
               placeholder="What needs doing" maxlength="200" />
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field">
        <!-- Optional: an activity may stand on its own, which is why the list leads with
             "No project — standalone". -->
        <label for="pat-project">Presale Project <span class="pat-optional">(optional)</span></label>
        <p-select id="pat-project" [options]="projectOptions" [(ngModel)]="form.presalesProjectId"
                  optionLabel="label" optionValue="value" appendTo="body"
                  placeholder="No project — standalone" [filter]="true"
                  filterPlaceholder="Search project..." />
      </div>

      <div class="pat-field">
        <label for="pat-owner-select">Sales Owner</label>
        <p-select id="pat-owner-select" [options]="employeeOptions" [(ngModel)]="form.ownerId"
                  optionLabel="label" optionValue="value" appendTo="body"
                  placeholder="Unassigned" [showClear]="true" [filter]="true"
                  filterPlaceholder="Search employee..." />
      </div>
    </div>

    <div class="pat-form-row">
      <!-- Who DOES the work, as against the Sales Owner above who drives the pursuit —
           routinely different people. One accountable name; several people can still be
           booked against the activity on the allocation screens. -->
      <div class="pat-field">
        <label for="pat-assigned-select">Assigned To</label>
        <p-select id="pat-assigned-select" [options]="employeeOptions"
                  [(ngModel)]="form.assignedToId"
                  optionLabel="label" optionValue="value" appendTo="body"
                  placeholder="Unassigned" [showClear]="true" [filter]="true"
                  filterPlaceholder="Search employee..." />
      </div>

      <!-- Spacer keeps Assigned To in the left column, matching the rows above. -->
      <div class="pat-field"></div>
    </div>

    <!-- Client, Presale PM and Cost Center belong to the project, so they are shown here
         rather than typed again: picking the project above fills them in, and they cannot
         drift out of step with the project record. -->
    <div class="pat-derived" *ngIf="selectedProjectContext as ctx">
      <div class="pat-derived-item">
        <span class="pat-derived-label">Client</span>
        <span class="pat-derived-value">{{ ctx.presalesClientName || '—' }}</span>
      </div>
      <div class="pat-derived-item">
        <span class="pat-derived-label">Presale Project Manager</span>
        <span class="pat-derived-value">{{ ctx.managerName || '—' }}</span>
      </div>
      <div class="pat-derived-item">
        <span class="pat-derived-label">Cost Center</span>
        <span class="pat-derived-value">{{ ctx.costCenterName || '—' }}</span>
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field">
        <label for="pat-start">Start Date</label>
        <p-datepicker inputId="pat-start" [(ngModel)]="startDateValue"
                      (ngModelChange)="onStartDateChange()"
                      dateFormat="dd M yy" [showIcon]="true" appendTo="body"
                      placeholder="Not scheduled" [showClear]="true" />
      </div>

      <div class="pat-field">
        <label for="pat-target">End Date</label>
        <!-- minDate binds the field itself (not an expression), so the reference stays
             stable between change-detection cycles. -->
        <p-datepicker inputId="pat-target" [(ngModel)]="targetDateValue"
                      dateFormat="dd M yy" [showIcon]="true" appendTo="body"
                      placeholder="Not scheduled" [showClear]="true"
                      [minDate]="startDateValue!" />
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field">
        <label for="pat-progress">Progress</label>
        <p-select id="pat-progress" [options]="progressOptions" [(ngModel)]="form.progress"
                  optionLabel="label" optionValue="value" appendTo="body" />
      </div>

      <div class="pat-field">
        <label for="pat-percent-top">Progress %</label>
        <p-inputNumber inputId="pat-percent-top" [(ngModel)]="form.progressPercentage"
                       [min]="0" [max]="100" [showButtons]="true" suffix="%" />
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field">
        <label for="pat-status-select">Status</label>
        <p-select id="pat-status-select" [options]="statusOptions" [(ngModel)]="form.status"
                  optionLabel="label" optionValue="value" appendTo="body"
                  placeholder="Not set" [showClear]="true" />
      </div>

      <!-- On by default for a new activity, since staffing one is the normal case. Cleared
           for work nobody books time against, which keeps it out of the allocation
           dropdown without deleting anything already booked. -->
      <div class="pat-field">
        <label for="pat-allocatable">Allocation</label>
        <div class="pat-check">
          <p-checkbox inputId="pat-allocatable" [(ngModel)]="form.isAllocatable"
                      [binary]="true" />
          <label for="pat-allocatable" class="pat-check-label">
            Available in Monthly Allocation
          </label>
        </div>
        <small class="pat-hint">
          Lets people be allocated to this activity on the Allocation page.
        </small>
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field pat-field--wide">
        <label for="pat-description">Description</label>
        <textarea id="pat-description" class="pat-textarea" rows="3"
                  [(ngModel)]="form.description"
                  placeholder="What this covers"></textarea>
      </div>
    </div>

    <div class="pat-form-row">
      <div class="pat-field pat-field--wide">
        <label for="pat-comments">Comments &amp; Notes</label>
        <textarea id="pat-comments" class="pat-textarea" rows="2"
                  [(ngModel)]="form.comments"
                  placeholder="Anything worth recording"></textarea>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="pat-dialog-footer">
      <span class="pat-dialog-footer-left">
        <p-button *ngIf="editingTaskId" label="Delete" icon="pi pi-trash" severity="danger"
                  [text]="true" (onClick)="confirmDeleteCurrent()" />
      </span>
      <span class="pat-dialog-footer-right">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary"
                  (onClick)="showTaskDialog = false" />
        <p-button label="Save" icon="pi pi-check" severity="success"
                  [loading]="saving" (onClick)="saveTask()" />
      </span>
    </div>
  </ng-template>
</p-dialog>
